import { useEscape } from "@/hooks/useKeyboardShortcut";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { newCrossIcon } from "@/icons";
import { BREAKPOINTS } from "@/settings";
import { Search } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "usehooks-ts";

import Spinner from "./atoms/Spinner/Spinner";

interface InputFieldProps {
  value: string;
  onChange: (value: string) => void;
  dismissSearch?: () => void;
  type: "email" | "search" | "default";
  placeholder?: string;
  isManuallyAddingPlayer?: boolean;
  isSearching?: boolean;
  children?: React.ReactNode;
  autoFocus?: boolean;
  readOnly?: boolean;
}

export const InputField = ({
  value,
  onChange,
  dismissSearch,
  type,
  placeholder,
  isSearching,
  children,
  autoFocus,
  readOnly,
}: InputFieldProps) => {
  const [hasClicked, setHasClicked] = useState(false);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const inputRef = useRef<HTMLInputElement>(null);
  const [focus, setFocus] = useState(false);
  const ref = useOutsideClick(() => {
    dismissSearch?.();
    setHasClicked(false);
  });
  useEscape(() => {
    dismissSearch?.();
    setHasClicked(false);
  });

  // Focus the input when hasClicked becomes true
  useEffect(() => {
    if (focus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [focus]);

  return (
    <div ref={ref}>
      {isMobile && type === "search" && !hasClicked ? (
        <button
          ref={ref}
          onClick={(e) => {
            e.stopPropagation();
            setHasClicked(true);
          }}
          className="w-9 h-15 flex items-center justify-center"
        >
          <Search className="h-5 w-5 text-gray-400" />
        </button>
      ) : (
        <div
          ref={ref}
          className="w-full h-9 flex items-center px-2 bg-new-gray-850 rounded-sm"
        >
          {type === "search" && (
            <Search className="h-5 w-5 text-gray-400 mr-1 shrink-0" />
          )}
          <input
            ref={inputRef}
            type={type}
            placeholder={placeholder}
            value={value}
            autoFocus={autoFocus}
            onChange={(e) => onChange(e.target.value)}
            className="flex-1 bg-transparent outline-none placeholder:text-dark-tertiary w-40 text-dark-secondary"
          />
          {isSearching ? (
            <div className="flex items-center">
              <Spinner fontSize="14px" />
            </div>
          ) : (
            children
          )}
          {value && !readOnly && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onChange("");
                setFocus(true);
              }}
              className="w-4 h-4 text-white hover:opacity-80"
            >
              {newCrossIcon}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default InputField;
